import "./style.css";
import { Form } from "react-bootstrap";
import { Campo } from "../../services/tipos";
import { TipoFormulario } from "../../services/constantes";
import { useRef, useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { FaCalendarAlt } from "react-icons/fa";

interface InputDateProps {
    campo: Campo;
    modo: TipoFormulario;
    apenasLeitura?: boolean;
    tipo?: "date" | "datetime-local";
    onBlur: () => void;
    getFieldValue: (campoNome: string) => string;
    handleInputChange: (nome: string, value: string) => void;
    hasError?: boolean;
}

const Container = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

const StyledInput = styled(Form.Control)<{ hasError?: boolean }>`
    font-size: 0.95rem;
    border-radius: 6px;
    border: 1px solid ${props => props.hasError ? '#dc3545' : '#ced4da'};
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
    padding-right: 2.5rem;

    &:focus {
        border-color: ${props => props.hasError ? '#dc3545' : '#80bdff'};
        box-shadow: 0 0 0 0.2rem rgba(${props => props.hasError ? '220, 53, 69' : '0, 123, 255'}, 0.25);
    }

    @media (max-width: 768px) {
        padding: 0.5rem;
        padding-right: 2.5rem;
        font-size: 0.9rem;
    }
`;

const HiddenDateInput = styled.input`
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
    pointer-events: none;
`;

const CalendarButton = styled.button<{ disabled?: boolean }>`
    position: absolute;
    right: 8px;
    background: none;
    border: none;
    color: ${props => props.disabled ? '#adb5bd' : '#6c757d'};
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: color 0.15s ease;

    &:hover:not(:disabled) {
        color: #1F467E;
    }

    &:focus {
        outline: none;
    }
`;

function formatToDisplay(isoValue: string, tipo: "date" | "datetime-local"): string {
    if (!isoValue) return "";

    if (tipo === "datetime-local") {
        // yyyy-MM-ddTHH:mm -> dd/MM/yyyy HH:mm
        const match = isoValue.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
        if (match) {
            return `${match[3]}/${match[2]}/${match[1]} ${match[4]}:${match[5]}`;
        }
        // Pode vir como yyyy-MM-dd
        const dateMatch = isoValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (dateMatch) {
            return `${dateMatch[3]}/${dateMatch[2]}/${dateMatch[1]}`;
        }
        return isoValue;
    }

    // date: yyyy-MM-dd -> dd/MM/yyyy
    const match = isoValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
        return `${match[3]}/${match[2]}/${match[1]}`;
    }
    return isoValue;
}

function isValidDate(day: number, month: number, year: number): boolean {
    if (year < 1900 || year > 2100) return false;
    if (month < 1 || month > 12) return false;
    if (day < 1) return false;
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

function parseDisplayToISO(display: string, tipo: "date" | "datetime-local"): string | null {
    if (!display) return "";

    if (tipo === "datetime-local") {
        // dd/MM/yyyy HH:mm -> yyyy-MM-ddTHH:mm
        const match = display.match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})$/);
        if (match) {
            const [, dd, mm, yyyy, hh, min] = match;
            if (!isValidDate(+dd, +mm, +yyyy)) return null;
            if (+hh > 23 || +min > 59) return null;
            return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
        }
        // Parcial: dd/MM/yyyy -> tenta sem hora
        const dateMatch = display.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (dateMatch) {
            const [, dd, mm, yyyy] = dateMatch;
            if (!isValidDate(+dd, +mm, +yyyy)) return null;
            return `${yyyy}-${mm}-${dd}T00:00`;
        }
        return null;
    }

    // date: dd/MM/yyyy -> yyyy-MM-dd
    const match = display.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (match) {
        const [, dd, mm, yyyy] = match;
        if (!isValidDate(+dd, +mm, +yyyy)) return null;
        return `${yyyy}-${mm}-${dd}`;
    }
    return null;
}

function applyDateMask(raw: string, tipo: "date" | "datetime-local"): string {
    // Remove tudo que não for dígito
    const digits = raw.replace(/\D/g, "");

    if (tipo === "datetime-local") {
        // dd/MM/yyyy HH:mm -> max 12 dígitos
        let result = "";
        for (let i = 0; i < Math.min(digits.length, 12); i++) {
            if (i === 2 || i === 4) result += "/";
            if (i === 8) result += " ";
            if (i === 10) result += ":";
            result += digits[i];
        }
        return result;
    }

    // dd/MM/yyyy -> max 8 dígitos
    let result = "";
    for (let i = 0; i < Math.min(digits.length, 8); i++) {
        if (i === 2 || i === 4) result += "/";
        result += digits[i];
    }
    return result;
}

export function InputDate(props: InputDateProps) {
    const tipo = props.tipo ?? "date";
    const hiddenRef = useRef<HTMLInputElement>(null);
    const isDisabled = (props.apenasLeitura === true) || (props.modo === TipoFormulario.tpVisualizacao);

    const currentISOValue = props.getFieldValue(props.campo.nome);
    const [displayValue, setDisplayValue] = useState(() => formatToDisplay(currentISOValue, tipo));

    // Sync display when ISO value changes externally
    useEffect(() => {
        if (currentISOValue === 'INVALID_DATE') return;
        const newDisplay = formatToDisplay(currentISOValue, tipo);
        setDisplayValue(newDisplay);
    }, [currentISOValue, tipo]);

    const expectedLength = tipo === "datetime-local" ? 16 : 10;

    const handleTextChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const masked = applyDateMask(e.target.value, tipo);
        setDisplayValue(masked);

        if (masked.length !== 0 && masked.length !== expectedLength) return;

        const iso = parseDisplayToISO(masked, tipo);
        props.handleInputChange(props.campo.nome, iso !== null ? iso : 'INVALID_DATE');
    }, [tipo, expectedLength, props.handleInputChange, props.campo.nome]);

    const handleTextBlur = useCallback(() => {
        if (displayValue.length !== 0 && displayValue.length !== expectedLength) {
            const iso = parseDisplayToISO(displayValue, tipo);
            if (iso) {
                setDisplayValue(formatToDisplay(iso, tipo));
                props.handleInputChange(props.campo.nome, iso);
            } else {
                props.handleInputChange(props.campo.nome, 'INVALID_DATE');
            }
        }
        props.onBlur();
    }, [displayValue, expectedLength, tipo, props.handleInputChange, props.campo.nome, props.onBlur]);

    const handleCalendarChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (!value) return;

        // Limita ano a 4 dígitos
        let finalValue = value;
        if (value.includes("T")) {
            const [datePart, timePart] = value.split("T");
            let [year, month, day] = datePart.split("-");
            if (year.length > 4) year = year.slice(0, 4);
            finalValue = `${year}-${month}-${day}T${timePart}`;
        } else {
            const [year, month, day] = value.split("-");
            if (year && year.length > 4) {
                finalValue = `${year.slice(0, 4)}-${month}-${day}`;
            }
        }

        props.handleInputChange(props.campo.nome, finalValue);
        // Ao escolher pela picker do calendário o input de texto não dispara onBlur,
        // então sem isto o onCampoDigitar nunca rodava nesse caminho.
        props.campo.onCampoDigitar && props.campo.onCampoDigitar(finalValue);
    }, [props.handleInputChange, props.campo]);

    const openCalendar = useCallback(() => {
        if (hiddenRef.current && !isDisabled) {
            hiddenRef.current.showPicker();
        }
    }, [isDisabled]);

    const placeholder = tipo === "datetime-local" ? "dd/mm/aaaa hh:mm" : "dd/mm/aaaa";

    return (
        <Container>
            <StyledInput
                type="text"
                disabled={isDisabled}
                required={props.campo.required}
                value={displayValue}
                onChange={handleTextChange}
                onBlur={handleTextBlur}
                hasError={props.hasError}
                placeholder={placeholder}
                maxLength={tipo === "datetime-local" ? 16 : 10}
                className="custom-text-input"
            />
            <HiddenDateInput
                ref={hiddenRef}
                type={tipo}
                value={currentISOValue}
                onChange={handleCalendarChange}
                tabIndex={-1}
            />
            <CalendarButton
                type="button"
                onClick={openCalendar}
                disabled={isDisabled}
                title="Abrir calendário"
                tabIndex={-1}
            >
                <FaCalendarAlt size={14} />
            </CalendarButton>
        </Container>
    );
}
